import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, switchMap, withLatestFrom } from 'rxjs';
import { PricingActions } from '@states/pricing/pricing.action-types';
import { AddOnOptions, CameraOptions, DeviceOptions, LicenseTerms, Pricing, PricingCount, StorageOptions } from '@models/pricing.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { PriceCalculatorService } from '../../price-calaulator/price-calculator.service';


@Injectable()
export class PricingEffect {

  public calculatePricing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PricingActions.calculatePricing),
      withLatestFrom(this.store$.pipe(select(state => state.pricingState))),
      switchMap(([, { locations, term, count }]) => {
        const _count: PricingCount = {
          box2tb: 0,
          box4tb: 0,
          box1u32tb: 0,
          box1u64tb: 0,
          box1u88tb: 0,
          camerasTurret5mp: 0,
          camerasBullet5mp: 0,
          camerasDome5mp: 0,
          camerasTurret8mp: 0,
          camerasBullet8mp: 0,
          camerasDome8mp: 0,
          camerasFisheye12mp: 0,
          camerasPtz: 0,
          camerasTurret5mpZoom: 0,
          ownCamerasTurret5mp: 0,
          ownCamerasBullet5mp: 0,
          ownCamerasDome5mp: 0,
          ownCamerasTurret8mp: 0,
          ownCamerasBullet8mp: 0,
          ownCamerasDome8mp: 0,
          ownCamerasFisheye12mp: 0,
          ownCamerasPtz: 0,
          ownCamerasTurret5mpZoom: 0,
          ownCore4TB: 0,
          ownCore1U30: 0,
          ownCore1U60: 0,
          ownCore1U90: 0,
          license: 0,
          verificationEvents: count.verificationEvents ?? 0,
          verificationGuns: count.verificationGuns ?? 0,
          verificationFire: count.verificationFire ?? 0,
          additionalSpeaker: count.additionalSpeaker ?? 0,
          additionalSpeakerLicense: count.additionalSpeakerLicense ?? 0,
          additionalSensorLicense: count.additionalSensorLicense ?? 0,
          additionalAppleTvLicense: count.additionalAppleTvLicense ?? 0,
          additionalAlertStorage: count.additionalAlertStorage ?? 0,
          additionalEventTagsStorage: count.additionalEventTagsStorage ?? 0,
          additionalWarrantyCameras: count.additionalWarrantyCameras ?? 0,
          additionalWarrantyCore4TB: count.additionalWarrantyCore4TB ?? 0,
          additionalWarrantyCore1U: count.additionalWarrantyCore1U ?? 0,
          cloudStorage30: count.cloudStorage30 ?? 0,
          cloudStorage60: count.cloudStorage60 ?? 0,
          cloudStorage90: count.cloudStorage90 ?? 0,
          cloudStorage180: count.cloudStorage180 ?? 0,
          cloudStorage365: count.cloudStorage365 ?? 0,
          externalStorage30: count.externalStorage30 ?? 0,
          externalStorage60: count.externalStorage60 ?? 0,
          externalStorage90: count.externalStorage90 ?? 0,
          externalStorage180: count.externalStorage180 ?? 0,
          externalStorage365: count.externalStorage365 ?? 0,
          metadataStorage30: count.metadataStorage30 ?? 0,
          metadataStorage60: count.metadataStorage60 ?? 0,
          metadataStorage90: count.metadataStorage90 ?? 0,
          metadataStorage180: count.metadataStorage180 ?? 0,
          metadataStorage365: count.metadataStorage365 ?? 0,
          snapshotStorage30: count.snapshotStorage30 ?? 0,
          snapshotStorage60: count.snapshotStorage60 ?? 0,
          snapshotStorage90: count.snapshotStorage90 ?? 0,
          snapshotStorage180: count.snapshotStorage180 ?? 0,
          snapshotStorage365: count.snapshotStorage365 ?? 0,
        };
        // Build _count from locations
        for (const location of locations) {
          const { box4tb, box1u32, box1u64, box1u88 } = this.priceCalculatorService.getLocationBoxes(location.info, location.include1u);
          _count.ownCore4TB += box4tb;
          _count.ownCore1U30 += box1u32;
          _count.ownCore1U60 += box1u64;
          _count.ownCore1U90 += box1u88;

          for (const info of location.info) {
            const calc = this.priceCalculatorService.getLocationPricing(info, term);
            _count.camerasTurret5mp += calc.count.camerasTurret5mp;
            _count.camerasTurret8mp += calc.count.camerasTurret8mp;
            _count.license += calc.count.license;
          }
        }

        // Use the same calculation as calculateCountPricing$ for consistency
        const pricing: Pricing = {
          boxes: 0,
          license: 0,
          cameras: 0,
          cloud: 0,
          total: 0,
          oneTimePrice: 0,
          regularPrice: 0,
          shipping: 0,
        };

        const regular: Pricing = {
          boxes: 0,
          license: 0,
          cameras: 0,
          cloud: 0,
          total: 0,
          regularPrice: 0,
          oneTimePrice: 0,
          shipping: 0,
        };

        this.calculatePricing(regular, _count, LicenseTerms.Years1);
        this.calculatePricing(pricing, _count, term);
        pricing.regularPrice = regular.total;

        return of(PricingActions.setPricing({ pricing, count: _count }));
      }),
    ),
  );

  public calculateCountPricing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PricingActions.calculateCountPricing),
      withLatestFrom(this.store$.pipe(select(state => state.pricingState))),
      switchMap(([, { count, term }]) => {
        // Regular
        const pricing: Pricing = {
          boxes: 0,
          license: 0,
          cameras: 0,
          cloud: 0,
          total: 0,
          oneTimePrice: 0,
          regularPrice: 0,
          shipping: 0,
        };

        const regular: Pricing = {
          boxes: 0,
          license: 0,
          cameras: 0,
          cloud: 0,
          total: 0,
          regularPrice: 0,
          oneTimePrice: 0,
          shipping: 0,
        };

        this.calculatePricing(regular, count, LicenseTerms.Years1);
        this.calculatePricing(pricing, count, term);
        pricing.regularPrice = regular.total;
        // pricing.shipping = this.getShippingPrice(count);

        return of(PricingActions.setPricing({ pricing, count }));
      }),
    ),
  );


  constructor(private actions$: Actions, private store$: Store<AppState>, private priceCalculatorService: PriceCalculatorService) {
  }

  private getShippingPrice(count: PricingCount) {
    const itemShippingPrice = 10;
    const serverShippingPrice = 50;
    const price = (
      count.box4tb + count.box2tb +
      count.camerasTurret5mp +
      count.camerasDome5mp +
      count.camerasBullet5mp +
      count.camerasTurret8mp +
      count.camerasDome8mp +
      count.camerasBullet8mp +
      count.camerasFisheye12mp +
      count.camerasPtz +
      count.camerasTurret5mpZoom +
      count.ownCamerasTurret5mp +
      count.ownCamerasDome5mp +
      count.ownCamerasBullet5mp +
      count.ownCamerasTurret8mp +
      count.ownCamerasDome8mp +
      count.ownCamerasBullet8mp +
      count.ownCamerasFisheye12mp +
      count.ownCamerasPtz +
      count.ownCamerasTurret5mpZoom +
      count.additionalSpeaker +
      count.box1u88tb +
      count.box1u64tb +
      count.box1u32tb +
      count.ownCore4TB
    ) * itemShippingPrice + (count.ownCore1U30 + count.ownCore1U60 + count.ownCore1U90) * serverShippingPrice;
    return price;
  }

  private getAddOnPrice(addOn: AddOnOptions) {
    return this.priceCalculatorService.getAddOnPrice(addOn);
  }

  private calculatePricing(pricing: Pricing, count: PricingCount, term: LicenseTerms) {
    const box2bPrice = this.priceCalculatorService.getBoxPrice(StorageOptions.Storage2TB, DeviceOptions.OrinNX16GB, term);
    const box4bPrice = this.priceCalculatorService.getBoxPrice(StorageOptions.Storage4TB, DeviceOptions.OrinNX16GB, term);
    const box1U32Price = this.priceCalculatorService.getBoxPrice(StorageOptions.Storage32TB, DeviceOptions.RACK1U32TB, term);
    const box1U64Price = this.priceCalculatorService.getBoxPrice(StorageOptions.Storage64TB, DeviceOptions.RACK1U64TB, term);
    const box1U88Price = this.priceCalculatorService.getBoxPrice(StorageOptions.Storage88TB, DeviceOptions.RACK1U88TB, term);
    const licensePrice = this.priceCalculatorService.getSWLicensePrice(term);
    const speakerLicensePrice = this.priceCalculatorService.getSpeakerLicensePrice(term);
    const sensorPrice = this.priceCalculatorService.getSensorLicensePrice(term);
    const camera5mpPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Resolution5MP, term);
    const camera8mpPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Resolution8MP, term);
    const cameraOpticalZoomPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.OpticalZoom5MP, term);
    const cameraFisheyePrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Fisheye12MP, term);
    const cameraPtzPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.PTZ, term);
    const ownCamera5mpPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Resolution5MP, term, true);
    const ownCamera8mpPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Resolution8MP, term, true);
    const ownCameraOpticalZoomPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.OpticalZoom5MP, term, true);
    const ownCameraFisheyePrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Fisheye12MP, term, true);
    const ownCameraPtzPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.PTZ, term, true);
    const ownCore4TBPrice = this.priceCalculatorService.getCameraPrice(CameraOptions.Core4TB, term, true);
    const ownCore1U30Price = this.priceCalculatorService.getCameraPrice(CameraOptions.Core1U30, term, true);
    const ownCore1U60Price = this.priceCalculatorService.getCameraPrice(CameraOptions.Core1U60, term, true);
    const ownCore1U90Price = this.priceCalculatorService.getCameraPrice(CameraOptions.Core1U90, term, true);


    const retentionPrice = this.priceCalculatorService.getRetentionPrice(count);
    const verificationPrice = this.priceCalculatorService.getVerificationPrice(count);
    const additionalOptionsPrice = this.priceCalculatorService.getAdditionalOptionsPrice(count, term);


    pricing.boxes += box2bPrice * count.box2tb + box4bPrice * count.box4tb +
      box1U32Price * count.box1u32tb + box1U64Price * count.box1u64tb + box1U88Price * count.box1u88tb;
    pricing.license += licensePrice * count.license;
    // [Sagi] Need to review
    pricing.cameras +=
      camera5mpPrice * count.camerasTurret5mp +
      camera5mpPrice * count.camerasDome5mp +
      camera5mpPrice * count.camerasBullet5mp +
      camera8mpPrice * count.camerasTurret8mp +
      camera8mpPrice * count.camerasDome8mp +
      camera8mpPrice * count.camerasBullet8mp +
      cameraPtzPrice * count.camerasPtz +
      cameraOpticalZoomPrice * count.camerasTurret5mpZoom +
      cameraFisheyePrice * count.camerasFisheye12mp;

    pricing.oneTimePrice = ownCamera5mpPrice * count.ownCamerasTurret5mp +
      ownCamera5mpPrice * count.ownCamerasDome5mp +
      ownCamera5mpPrice * count.ownCamerasBullet5mp +
      ownCamera8mpPrice * count.ownCamerasTurret8mp +
      ownCamera8mpPrice * count.ownCamerasDome8mp +
      ownCamera8mpPrice * count.ownCamerasBullet8mp +
      ownCameraPtzPrice * count.ownCamerasPtz +
      ownCore4TBPrice * count.ownCore4TB +
      ownCore1U30Price * count.ownCore1U30 +
      ownCore1U60Price * count.ownCore1U60 +
      ownCore1U90Price * count.ownCore1U90 +
      ownCameraOpticalZoomPrice * count.ownCamerasTurret5mpZoom +
      ownCameraFisheyePrice * count.ownCamerasFisheye12mp +
      count.additionalWarrantyCameras * 19 +
      count.additionalWarrantyCore4TB * 159 +
      count.additionalWarrantyCore1U * 499 +
      this.getAddOnPrice(AddOnOptions.Speaker) * count.additionalSpeaker;

    pricing.shipping = this.getShippingPrice(count);

    pricing.total += pricing.boxes + pricing.license + pricing.cameras;

    pricing.boxes *= this.priceCalculatorService.pricingTermToYears[term];
    pricing.license *= this.priceCalculatorService.pricingTermToYears[term];
    pricing.cameras *= this.priceCalculatorService.pricingTermToYears[term];
    pricing.total *= this.priceCalculatorService.pricingTermToYears[term];

    pricing.total += (retentionPrice + verificationPrice + additionalOptionsPrice) * this.priceCalculatorService.pricingTermToYears[term] + pricing.oneTimePrice;
  }
}
